import { makeAutoObservable, reaction, runInAction } from 'mobx';
import keyBy from 'lodash/keyBy';
import orderBy from 'lodash/orderBy';
import intersection from 'lodash/intersection';
import pick from 'lodash/pick';
import remove from 'lodash/remove';
import services from '../services';
import Automation from './models/automation';
import { extractErrorMessage, filterPreviewSyncUsers } from '../utils/helpers';
import {
    AUTOMATION_SCHEDULE_TYPE,
    AUTOMATION_WIZARD_STEPS,
    STATUS,
} from '../utils/constants';
import moment from 'moment';
import { translateTrainingScheduleType } from '../utils/scheduleType';
import { getDeliveryChannels } from '../utils/deliveryChannel';
import { Buffer } from 'buffer';
import { generateLoadList, generateLoadEntity, generateUpdateEntity } from '../utils/mobx';
import SelectionController from './SelectionController';

const activeStatuses = { [STATUS.SCHEDULED]: 1, [STATUS.ACTIVE]: 1 };
const completedStatuses = { [STATUS.COMPLETED]: 1 };

class AutomationStore {
    automations = [];
    latestTenAutomations = []; //Light-weight objects for the latest (10) updated trainings
    dashboardAutomations = [];
    loadingAutomations = false;
    loadingAutomationsInBackground = false;
    loadingDashboardAutomations = false;
    loadingDashboardAutomationsInBackground = false;
    loadingLatestTenAutomations = false;
    loadingAutomation = false;
    loadingAutomationStatus = false;
    loadingAutomationUserDetails = false;
    loadingCompanyData = false;
    loadingReminderCounter = false;

    reminderCounter = 0;
    lastReminders = [];
    sendAutomationEmails = [];
    perUserDetails = [];
    scenarios = [];
    scenariosTotal = 0;
    loadingScenarios = false;
    scenariosQueryKey = null;
    threatAreas = [];
    threatAreasTotal = 0;
    loadingThreatAreas = false;
    threatAreasQueryKey = null;
    scormUsers = [];
    scormUsersTotal = 0;
    loadingScormUsers = false;
    scormUsersQueryKey = null;
    listsProgress = [];
    listsProgressTotal = 0;
    loadingListsProgress = false;
    listsProgressQueryKey = null;
    trainingUsers = [];
    trainingUsersTotal = 0;
    loadingTrainingUsers = false;
    trainingUsersQueryKey = null;
    remindersOverview = [];
    remindersOverviewTotal = 0;
    loadingRemindersOverview = false;
    remindersOverviewQueryKey = null;
    scheduleOverview = null;
    loadingScheduleOverview = false;
    updatingScheduleEvents = [];
    updatingRemindersSettings = false;
    questions = [];
    rawTemplates = [];
    creatingAutomation = false;
    updatingAutomation = false;
    sendError = null;
    dueError = null;
    updatingDue = false;
    sendingPreview = false;
    automation = undefined;
    automationStatus = undefined;
    automationUserStatus = undefined;
    editingAutomation = undefined;
    loading = false;
    step = AUTOMATION_WIZARD_STEPS.GOAL;
    selectedTemplates = [];
    templateCustomized = false;
    allowedSteps = [];
    confirmedSteps = [];
    error = '';
    dismissedFirstTraining = false;
    perUserDetailsLoaded = false;
    perUserDetailsEmails = [];
    slackConnected = false;
    teamsConnected = false;
    searchValue = '';
    subjectFilter = [];
    employeeFilter = [];
    fromEndDateFilter = null;
    toEndDateFilter = null;
    toStartDateFilter = null;
    fromStartDateFilter = null;
    sortBy = '_rawData.updated';

    //By user
    userAutomations = [];
    loadingUserAutomations = false;

    chartMetrics = [];
    chartMetricsLoading = false;

    chartMetricsMigrating = false;
    loadingPreviewSyncLists = null;

    loadingSigninLink = false;
    lastSigninLinkUrl = null;

    constructor(
        listStore,
        catalogStore,
        mainStore,
        commonStore,
        companyStore,
        authStore,
        historyStore,
        brandingStore
    ) {
        makeAutoObservable(this);
        this.listStore = listStore;
        this.catalogStore = catalogStore;
        this.mainStore = mainStore;
        this.commonStore = commonStore;
        this.companyStore = companyStore;
        this.historyStore = historyStore;
        this.authStore = authStore;
        this.brandingStore = brandingStore;
        this.dismissedFirstTraining = !!window.localStorage.getItem(
            'dismissedFirstTraining'
        );

        this.previewUsersToAddSelection = new SelectionController(
            this.previewUsersToAddCurrentPage,
            this.toAddPageData,
            (x) => x.email,
            this.previewUsersToAdd.length
        );

        this.previewUsersToRemoveSelection = new SelectionController(
            this.previewUsersToRemoveCurrentPage,
            this.toRemovePageData,
            (x) => x.email,
            this.previewUsersToRemove.length
        );

        reaction(
            () => this.previewUsersToAddCurrentPage,
            (data) => {
                this.previewUsersToAddSelection.setData(
                    data,
                    this.previewUsersToAdd.length
                );
            }
        );

        reaction(
            () => this.previewUsersToRemoveCurrentPage,
            (data) => {
                this.previewUsersToRemoveSelection.setData(
                    data,
                    this.previewUsersToRemove.length
                );
            }
        );

        reaction(
            () => this.mainStore.currentCompany,
            (currentCompany) => {
                if (currentCompany) {
                    this.clearFilters();
                }
                // Clear list data and loading state so list view can load after company switch
                // (otherwise dashboard load can leave loadingAutomations true and list load returns early)
                runInAction(() => {
                    this.automations.replace([]);
                    this.latestTenAutomations.replace([]);
                    this.dashboardAutomations.replace([]);
                    this.loadingAutomations = false;
                    this.loadingAutomationsInBackground = false;
                    this.loadingDashboardAutomations = false;
                    this.loadingDashboardAutomationsInBackground = false;
                    this.loadingLatestTenAutomations = false;
                });
            }
        );
    }

    get templates() {
        return this.rawTemplates.filter(
            (x) => !x.definition || !x.definition.isAddOn
        );
    }

    get addOns() {
        return this.rawTemplates.filter(
            (x) => x.definition && x.definition.isAddOn
        );
    }

    setError = (error, type) => {
        if (type === 'updateDue') {
            this.dueError = error;
        } else {
            this.error = error;
        }
    };

    get filteredAutomations() {
        let ordered = orderBy(this.automations, this.sortBy, 'desc');

        let filtered = ordered.filter((x) => {
            if (x.isScorm) return false;


            if (
                this.subjectFilter.length > 0 &&
                intersection(
                    Object.keys(keyBy(x.subjectsNames, 'id')).map(Number),
                    this.subjectFilter
                ).length === 0
            )
                return false;

            if (
                this.employeeFilter.length > 0 &&
                x.lists !== null &&
                intersection(x._rawData.lists, this.employeeFilter).length === 0
            )
                return false;

            if (
                this.searchValue.length > 0 &&
                x.name !== null &&
                !x.name.toLowerCase().includes(this.searchValue.toLowerCase())
            )
                return false;

            if (
                this.fromStartDateFilter !== null &&
                this.toStartDateFilter !== null &&
                x._rawData.firstBatch !== null &&
                !moment(x._rawData.firstBatch).isBetween(
                    this.fromStartDateFilter,
                    this.toStartDateFilter
                )
            ) {
                return false;
            }

            return !(this.fromEndDateFilter !== null &&
                this.toEndDateFilter !== null &&
                x._rawData.lastBatch !== null &&
                !moment(x._rawData.lastBatch).isBetween(
                    this.fromEndDateFilter,
                    this.toEndDateFilter
                ));


        });
        return filtered;
    }

    get allTab() {
        return this.filteredAutomations;
    }

    get activeTab() {
        return this.filteredAutomations.filter((x) => {
            return activeStatuses[x.status];
        });
    }

    get completedTab() {
        return this.filteredAutomations.filter((x) => {
            return completedStatuses[x.status];
        });
    }

    get draftTab() {
        return this.filteredAutomations.filter((x) => {
            return x.status === STATUS.DRAFT;
        });
    }

    get steps() {
        return [
            {
                number: AUTOMATION_WIZARD_STEPS.GOAL,
                title:
                    this.automation && this.automation.status === STATUS.ACTIVE
                        ? 'Template'
                        : 'Choose template',
            },
            {
                number: AUTOMATION_WIZARD_STEPS.CONTENT,
                title: 'Review Subjects',
            },
            ...(this.automation && this.automation.isScorm
                ? [
                      {
                          number: AUTOMATION_WIZARD_STEPS.LANGUAGES,
                          title: 'Available Languages',
                      },
                  ]
                : [
                      {
                          number: AUTOMATION_WIZARD_STEPS.EMPLOYEES,
                          title: 'Select Employees',
                      },
                      {
                          number: AUTOMATION_WIZARD_STEPS.SCHEDULE,
                          title: 'Schedule',
                      },
                      {
                          number: AUTOMATION_WIZARD_STEPS.REMINDERS,
                          title: 'Reminders',
                      },
                      {
                          number: AUTOMATION_WIZARD_STEPS.INTROMESSAGE,
                          title: 'Intro Message',
                      },
                  ]),
            {
                number: AUTOMATION_WIZARD_STEPS.FINALIZE,
                title: 'Summary',
            },
        ];
    }
    get filters() {
        return [
            {
                label: 'Subjects',
                value: this.subjectFilter,
                onChange: (value) => {
                    this.setSubjectFilter(value);
                },
                options: Object.values(this.catalogStore.allSubjectsIndex || {})
                    .filter((subject) => subject && subject.title)
                    .map((subject) => ({
                        value: subject.id,
                        label: subject.title,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label)),
            },
            {
                label: 'Employee lists',
                value: this.employeeFilter,
                onChange: (value) => {
                    this.setEmployeeFilter(value);
                },
                options: this.listStore.listsWithUsers
                    .map((list) => ({
                        value: list.id,
                        label: list.name,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label)),
            },
            {
                type: 'date',
                label: 'Start date',
                startValue: this.fromStartDateFilter,
                endValue: this.toStartDateFilter,
                onStartChange: (value) => {
                    const fixed = value?.set({ h: 0, m: 0 });
                    this.setFromStartDateFilter(fixed ?? value);
                },
                onEndChange: (value) => {
                    const fixed = value?.set({ h: 23, m: 59 });
                    this.setToStartDateFilter(fixed ?? value);
                },
            },
            {
                type: 'date',
                label: 'End date',
                startValue: this.fromEndDateFilter,
                endValue: this.toEndDateFilter,
                onStartChange: (value) => {
                    const fixed = value?.set({ h: 0, m: 0 });
                    this.setFromEndDateFilter(fixed ?? value);
                },
                onEndChange: (value) => {
                    const fixed = value?.set({ h: 23, m: 59 });
                    this.setToEndDateFilter(fixed ?? value);
                },
            },
        ];
    }
    clearFilters () {
        this.setSubjectFilter([]);
        this.setEmployeeFilter([]);
        this.setFromEndDateFilter(null);
        this.setToEndDateFilter(null);
        this.setToEndDateFilter(null);
        this.setFromStartDateFilter(null);
        this.setToStartDateFilter(null);
    }
    setSubjectFilter(subjectFilter) {
        this.subjectFilter = subjectFilter;
    }

    setSearchValue(searchValue) {
        this.searchValue = searchValue;
    }

    setEmployeeFilter(employeeFilter) {
        this.employeeFilter = employeeFilter;
    }

    setFromEndDateFilter(fromEndDateFilter) {
        this.fromEndDateFilter = fromEndDateFilter;
    }

    setToEndDateFilter(toEndDateFilter) {
        this.toEndDateFilter = toEndDateFilter;
    }

    setFromStartDateFilter(fromStartDateFilter) {
        this.fromStartDateFilter = fromStartDateFilter;
    }

    setToStartDateFilter(toStartDateFilter) {
        this.toStartDateFilter = toStartDateFilter;
    }

    setSortBy(sortBy) {
        this.sortBy = sortBy;
    }

    setStep(step) {
        this.error = '';
        this.step = step;
        this.commonStore.analyticsEvent(
            'Training-wizard',
            'step',
            `step-${step}`
        );
    }

    addStep(addStep, extraCondition = true) {
        if (!this.allowedSteps.includes(addStep) && extraCondition) {
            this.allowedSteps.push(addStep);
            return true;
        }
        return false;
    }

    removeStep(removeStep, extraCondition = true) {
        const removedSteps = remove(
            this.allowedSteps,
            (step) => step === removeStep && extraCondition
        );
        return removedSteps.length > 0;
    }

    get nextStepNumber() {
        let index = this.steps.findIndex((x) => x.number === this.step);
        return this.steps[index + 1]
            ? this.steps[index + 1].number
            : this.step + 1;
    }

    dismissFirstTraining() {
        this.dismissedFirstTraining = true;
        window.localStorage.setItem('dismissedFirstTraining', true);
    }

    get rawTemplatesIndex() {
        return keyBy(this.rawTemplates, 'id');
    }

    get selectedTemplatesData() {
        return this.selectedTemplates.map((x) => this.rawTemplatesIndex[x]);
    }

    setTemplate(templateId) {
        this.selectedTemplates = [templateId];
        let template = this.rawTemplates.find((x) => x.id === templateId);
        if (template) {
            this.automation.templateCustomized = false;
            this.commonStore.analyticsEvent(
                'Training-wizard',
                'choose-template',
                `template-${template.title}`
            );
            //A template contains both subjects and disabledSubjects
            this.automation.selectedVideosIds.replace(
                template.subjects.concat(template.disabledSubjects)
            );
            this.automation.setFrequency(template.frequency);
            this.automation.templateTip = template.tip;
        }

        if (this.selectedTemplates.length > 0) {
            this.addStep(AUTOMATION_WIZARD_STEPS.CONTENT);
        } else {
            this.removeStep(AUTOMATION_WIZARD_STEPS.CONTENT);
        }
    }

    setTemplateAsCustom() {
        const customTemplate = this.templates.find((x) => x.custom);
        if (customTemplate) this.selectedTemplates = [customTemplate.id];
    }

    toggleFinalStep(form) {
        if (this.automation && this.automation.isScorm) {
            this.addStep(
                AUTOMATION_WIZARD_STEPS.FINALIZE,
                this.allowedSteps.includes(AUTOMATION_WIZARD_STEPS.LANGUAGES)
            );
            this.removeStep(
                AUTOMATION_WIZARD_STEPS.FINALIZE,
                !this.allowedSteps.includes(AUTOMATION_WIZARD_STEPS.LANGUAGES)
            );
            return;
        }
        const enabled = this.automation && this.automation.introMessageEnabled;
        const messageEmpty =
            form &&
            (form.$('message').$value === '<p><br></p>' ||
                form.$('message').$value === '');

        if (
            this.allowedSteps.includes(AUTOMATION_WIZARD_STEPS.INTROMESSAGE) &&
            (!enabled || !messageEmpty)
        ) {
            this.addStep(AUTOMATION_WIZARD_STEPS.FINALIZE);
        } else {
            this.removeStep(AUTOMATION_WIZARD_STEPS.FINALIZE);
        }
    }

    toggleRemindersStep() {
        const dueDateIsOK = this.automation.dueDate
            ? moment(this.automation.dueDate).isAfter(
                  this.automation.scheduleType ===
                      AUTOMATION_SCHEDULE_TYPE.FUTURE
                      ? this.automation.deliverySchedule.length > 0
                          ? moment(
                                this.automation.deliverySchedule[
                                    this.automation.deliverySchedule.length - 1
                                ].date
                            )
                          : moment(this.automation.date)
                      : moment()
              )
            : true;

        if (
            (this.automation.scheduleType ===
                AUTOMATION_SCHEDULE_TYPE.DOWNLOAD_LINKS ||
                (this.automation.scheduleType ===
                    AUTOMATION_SCHEDULE_TYPE.FUTURE &&
                    this.automation.date !== null &&
                    dueDateIsOK &&
                    moment().isBefore(this.automation.date)) ||
                this.automation.scheduleType ===
                    AUTOMATION_SCHEDULE_TYPE.NOW) &&
            this.automation.channel !== 0 &&
            this.allowedSteps.includes(AUTOMATION_WIZARD_STEPS.SCHEDULE)
        ) {
            this.addStep(AUTOMATION_WIZARD_STEPS.REMINDERS);
        } else {
            this.removeStep(AUTOMATION_WIZARD_STEPS.REMINDERS);
        }
    }

    get reminderStepValid() {
        if (!this.automation) return false;

        if (!this.automation.reminders_enabled) return true;

        return (
            this.automation.reminders_steps.length > 0 &&
            this.automation.reminders_channel !== 0
        );
    }

    toggleIntroMessageStep() {
        if (
            this.reminderStepValid &&
            this.allowedSteps.includes(AUTOMATION_WIZARD_STEPS.REMINDERS)
        ) {
            this.addStep(AUTOMATION_WIZARD_STEPS.INTROMESSAGE);
        } else {
            this.removeStep(AUTOMATION_WIZARD_STEPS.INTROMESSAGE);
        }
    }

    toggleRecipientsAndLanguagesStep() {
        if (this.automation && this.automation.selectedVideosIds.length > 0) {
            this.addStep(
                AUTOMATION_WIZARD_STEPS.LANGUAGES,
                this.automation.isScorm
            );
            this.addStep(
                AUTOMATION_WIZARD_STEPS.EMPLOYEES,
                !this.automation.isScorm
            );
        }

        if (
            !this.automation ||
            this.automation.selectedVideosIds.length === 0
        ) {
            this.removeStep(
                AUTOMATION_WIZARD_STEPS.LANGUAGES,
                this.automation.isScorm
            );
            this.removeStep(
                AUTOMATION_WIZARD_STEPS.EMPLOYEES,
                !this.automation.isScorm
            );
        }
    }

    toggleScheduleStep() {
        if (this.automation && this.automation.selectedUserCount > 0)
            this.addStep(AUTOMATION_WIZARD_STEPS.SCHEDULE);

        if (!this.automation || this.automation.selectedUserCount === 0) {
            this.removeStep(AUTOMATION_WIZARD_STEPS.SCHEDULE);
        }
    }

    async sendPreview(
        companyId,
        targetEmail,
        previewType,
        selectedEvents,
        introMessage
    ) {
        this.sendingPreview = true;
        this.sendError = '';
        try {
            const data = { targetEmail };
            if (previewType === 1) {
                data.previewSubjects = this.automation.deliverySchedule.map(
                    (x) => {
                        return {
                            index: x.tempId,
                            subjectIds: x.subjects.map(
                                (s) => s.subject_id || s.id
                            ),
                        };
                    }
                );
            } else {
                data.previewSubjects = this.automation.deliverySchedule
                    .filter((x) => selectedEvents.includes(x.tempId))
                    .map((x) => {
                        return {
                            index: x.tempId,
                            subjectIds: x.subjects.map(
                                (s) => s.subject_id || s.id
                            ),
                        };
                    });
            }
            if (this.automation.introMessageEnabled)
                data.introductoryData = introMessage;

            await services.Companies.automationsService(companyId).sendPreview(
                data
            );
            this.commonStore.analyticsEvent(
                'Training-wizard',
                'preview-sent',
                `company-${companyId}`
            );
            return true;
        } catch (e) {
            this.sendError = extractErrorMessage(e);
        } finally {
            this.sendingPreview = false;
        }
    }

    async loadRemindersCounter(companyId, automationId, reminderOption) {
        if (this.loadingReminderCounter) return;
        this.loadingReminderCounter = true;
        this.reminderCounter = null;
        this.lastReminders.replace([]);
        try {
            let result = await services.Companies.automationsService(
                companyId
            ).loadingRemindersCounter(automationId, reminderOption);
            this.reminderCounter = result.remindersCount;
            this.lastReminders.replace(result.lastReminders);
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.loadingReminderCounter = false;
        }
    }

    async sendReminders(companyId, automationId, reminderOption) {
        try {
            return await services.Companies.automationsService(
                companyId
            ).sendReminders(automationId, reminderOption);
        } catch (e) {
            this.error = extractErrorMessage(e);
        }
    }

    get scormAutomations() {
        return this.automations.filter((x) => x.isScorm);
    }


    get filteredScormAutomations() {
        return this.scormAutomations.filter((x) => {
            if (
                this.subjectFilter.length > 0 &&

                intersection(
                    (x._rawData.subjects || []).map(s => s.subject_id || s.id).map(Number),
                    this.subjectFilter
                ).length === 0
            )
                return false;

            if (
                this.searchValue.length > 0 &&
                x.name !== null &&
                !x.name.toLowerCase().includes(this.searchValue.toLowerCase())
            )
                return false;

            return !(this.employeeFilter.length > 0 ||
                this.fromStartDateFilter !== null ||
                this.toStartDateFilter !== null ||
                this.fromEndDateFilter !== null ||
                this.toEndDateFilter !== null);


        });
    }

    get isBothAutomationTypes() {
        return (
            this.automations.filter((x) => x.isScorm).length > 0 &&
            this.automations.filter((x) => !x.isScorm).length > 0
        );
    }

    get isScormOnly() {
        return (
            this.automations.filter((x) => x.isScorm).length > 0 &&
            this.automations.filter((x) => !x.isScorm).length === 0
        );
    }

    async prepareWizard(
        companyId,
        copyFrom,
        editId,
        isScorm,
        initialData,
        recommendationMode = false,
        enabledLanguages = []
    ) {
        if (this.loadingCompanyData) return;
        this.loadingCompanyData = true;
        this.error = null;
        this.automations.clear();
        this.automation = null;
        this.isScorm = isScorm;
        this.selectedTemplates = [];
        try {
            await this.catalogStore.loadCatalog(companyId);
            let templates = await services.Companies.templatesService(
                companyId
            ).list();
            this.rawTemplates.replace(templates);
            let questions = await services.Companies.questionsService(
                companyId
            ).list();
            this.questions.replace(questions);

            await this.listStore.loadCompanyLists(companyId);
            await this.companyStore.loadIntegrationData(companyId, 'slack');
            await this.companyStore.loadIntegrationData(companyId, 'teams');
            let fromData;

            this.slackConnected =
                this.companyStore.slackDataIndex[companyId] &&
                this.companyStore.slackDataIndex[companyId].connected;
            this.teamsConnected =
                this.companyStore.teamsDataIndex[companyId] &&
                this.companyStore.teamsDataIndex[companyId].connected &&
                this.companyStore.teamsDataIndex[companyId].enabled === '1';

            if (copyFrom) {
                let fromAutomation =
                    await services.Companies.automationsService(
                        companyId
                    ).fetch(copyFrom);

                fromData = {
                    ...pick(
                        fromAutomation,
                        'definition',
                        'frequency',
                        'lists',
                        'events',
                        'reminders_enabled',
                        'reminders_settings',
                        'reminders_steps',
                        'scheduleType',
                        'subjects',
                        'template_id'
                    ),
                    name: fromAutomation.name + ' Copy',
                    channel:
                        fromAutomation.sendOptions === null
                            ? 0
                            : fromAutomation.sendOptions.channel || 0,
                    reminders_channel:
                        fromAutomation.reminders_settings === null
                            ? 0
                            : fromAutomation.reminders_settings.channel || 0,
                    id: null,
                    isCopy: true,
                };
                this.setStep(AUTOMATION_WIZARD_STEPS.CONTENT);
                if (isScorm) {
                    this.allowedSteps.replace([
                        AUTOMATION_WIZARD_STEPS.GOAL,
                        AUTOMATION_WIZARD_STEPS.CONTENT,
                        AUTOMATION_WIZARD_STEPS.LANGUAGES,
                        AUTOMATION_WIZARD_STEPS.FINALIZE,
                    ]);
                } else
                    this.allowedSteps.replace([
                        AUTOMATION_WIZARD_STEPS.GOAL,
                        AUTOMATION_WIZARD_STEPS.CONTENT,
                        AUTOMATION_WIZARD_STEPS.EMPLOYEES,
                        AUTOMATION_WIZARD_STEPS.SCHEDULE,
                        AUTOMATION_WIZARD_STEPS.REMINDERS,
                        AUTOMATION_WIZARD_STEPS.INTROMESSAGE,
                        AUTOMATION_WIZARD_STEPS.FINALIZE,
                    ]);
                this.selectedTemplates.replace([fromData.template_id]);
            } else if (editId) {
                fromData = await services.Companies.automationsService(
                    companyId
                ).fetch(editId);

                this.setStep(AUTOMATION_WIZARD_STEPS.GOAL);
                this.allowedSteps.replace([
                    AUTOMATION_WIZARD_STEPS.GOAL,
                    AUTOMATION_WIZARD_STEPS.CONTENT,
                    AUTOMATION_WIZARD_STEPS.EMPLOYEES,
                    AUTOMATION_WIZARD_STEPS.SCHEDULE,
                    AUTOMATION_WIZARD_STEPS.REMINDERS,
                    AUTOMATION_WIZARD_STEPS.INTROMESSAGE,
                    AUTOMATION_WIZARD_STEPS.LANGUAGES,
                    AUTOMATION_WIZARD_STEPS.FINALIZE,
                ]);
                this.selectedTemplates.replace([fromData.template_id]);
            } else if (initialData) {
                fromData = initialData;
                console.log(fromData);
                this.setStep(AUTOMATION_WIZARD_STEPS.SCHEDULE);
                this.allowedSteps.replace([
                    AUTOMATION_WIZARD_STEPS.GOAL,
                    AUTOMATION_WIZARD_STEPS.CONTENT,
                    AUTOMATION_WIZARD_STEPS.LANGUAGES,
                    AUTOMATION_WIZARD_STEPS.EMPLOYEES,
                    AUTOMATION_WIZARD_STEPS.SCHEDULE,
                ]);
                this.selectedTemplates.replace([-1]);
            } else {
                this.setStep(AUTOMATION_WIZARD_STEPS.GOAL);
                this.allowedSteps.replace([AUTOMATION_WIZARD_STEPS.GOAL]);
            }

            this.automation = new Automation(
                this.listStore,
                this.catalogStore,
                this.mainStore,
                companyId,
                this.questionsIndex,
                fromData,
                this.mainStore.isTrial,
                this.mainStore.havePartnerSubscription,
                null,
                enabledLanguages
            );
            this.automation.isScorm = isScorm;
            if (recommendationMode) {
                this.automation.subjectsPerInterval = 2;
                this.automation.name = 'Untitled Training';
            } else if (this.automation.subjectsPerInterval == null) {
                this.automation.subjectsPerInterval = 3;
            }

            if (recommendationMode && isScorm){
                this.addStep(AUTOMATION_WIZARD_STEPS.FINALIZE);
                this.setStep(AUTOMATION_WIZARD_STEPS.LANGUAGES);
            }
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.loadingCompanyData = false;
            this.commonStore.saveAnalyticsEvent(
                'training',
                'creation_started',
                {
                    sourcePage: this.historyStore.previousPath,
                }
            );
        }
    }

    loadUserAutomations = generateLoadList(
        'loadUserAutomations',
        this,
        'loadingUserAutomations',
        (companyId, email) =>
            !email
                ? Promise.resolve([])
                : services.Companies.automationsService(companyId).userTrainings(email),
        'userAutomations',
        (result, companyId, email) =>
            (result || []).map(
                (x) =>
                    new Automation(
                        this.listStore,
                        this.catalogStore,
                        this.mainStore,
                        companyId,
                        this.questionsIndex,
                        x,
                        this.mainStore.isTrial,
                        null,
                        email
                    )
            )
    );

    loadListAutomations = generateLoadList(
        'loadListAutomations',
        this,
        'loadingListAutomations',
        (companyId, listId) =>
            !listId
                ? Promise.resolve([])
                : services.Companies.listsService(companyId).trainings(listId),
        'listAutomations',
        (result, companyId) =>
            (result || []).map(
                (x) =>
                    new Automation(
                        this.listStore,
                        this.catalogStore,
                        this.mainStore,
                        companyId,
                        this.questionsIndex,
                        x,
                        this.mainStore.isTrial
                    )
            )
    );

    get usersLatestTraining() {
        if (!this.userAutomations || this.userAutomations < 1) return null;

        const ordered = this.userAutomations
            .slice()
            .filter(
                (training) =>
                    training.status !== STATUS.SCHEDULED &&
                    training.status !== STATUS.DRAFT &&
                    training._rawData.subjectsSent > 0
            )
            .sort(
                (a, b) =>
                    new Date(b._rawData.created || 0) -
                    new Date(a._rawData.created || 0)
            );
        return ordered[0];
    }

    loadLatestTenAutomations = generateLoadList(
        'loadingLatestTenAutomations',
        this,
        'loadingLatestTenAutomations',
        (companyId) =>
            services.Companies.automationsService(companyId).listLatestTen(),
        'latestTenAutomations',
        (result, companyId) =>
            (result || []).map(
                (x) =>
                    new Automation(
                        this.listStore,
                        this.catalogStore,
                        this.mainStore,
                        companyId,
                        this.questionsIndex,
                        x,
                        this.mainStore.isTrial
                    )
            )
    );

    // d = show drafts (boolean), p = page number, n = number of rows per page
    loadAutomations = generateLoadList(
        'loadingAutomations',
        this,
        'loadingAutomations',
        (companyId, d, p, n) =>
            services.Companies.automationsService(companyId).list({ d, p, n }),
        'automations',
        (result, companyId) =>
            (result || []).map(
                (x) =>
                    new Automation(
                        this.listStore,
                        this.catalogStore,
                        this.mainStore,
                        companyId,
                        this.questionsIndex,
                        x,
                        this.mainStore.isTrial
                    )
            ),
        { backgroundFlag: 'loadingAutomationsInBackground' }
    );

    loadDashboardAutomations = generateLoadList(
        'loadingDashboardAutomations',
        this,
        'loadingDashboardAutomations',
        (companyId) =>
            services.Companies.automationsService(companyId).list({
                d: false,
                p: 0,
                n: 3,
            }),
        'dashboardAutomations',
        (result, companyId) =>
            (result || []).map(
                (x) =>
                    new Automation(
                        this.listStore,
                        this.catalogStore,
                        this.mainStore,
                        companyId,
                        this.questionsIndex,
                        x,
                        this.mainStore.isTrial
                    )
            ),
        { backgroundFlag: 'loadingDashboardAutomationsInBackground' }
    );

    setChartMetric(data) {
        this.chartMetrics.replace(data);
        this.chartMetricsMigrating = false;
    }

    insertChartMetric(data) {
        if (this.chartMetricsMigrating) {
            return;
        }
        //try to find existing metric
        let existing = this.chartMetrics.find(
            (x) => x.lastTimestamp === data.lastTimestamp
        );
        if (!existing) {
            this.chartMetrics.push(data);
        } else {
            Object.assign(existing, data);
        }
    }

    loadAutomationChartMetrics = generateLoadList(
        'chartMetrics',
        this,
        'chartMetricsLoading',
        async (companyId, automationId) => {
            const result = await services.Companies.automationsService(
                companyId
            ).chartMetrics(automationId);
            if (result && result.migration) {
                runInAction(() => {
                    this.chartMetricsMigrating = true;
                });
                return [];
            } else return result;
        },
        'chartMetrics'
    );

    async loadScenarios(companyId, automationId, params = {}) {
        const queryKey = JSON.stringify({ companyId, automationId, params });
        if (this.loadingScenarios && this.scenariosQueryKey === queryKey) return;
        this.scenariosQueryKey = queryKey;
        this.loadingScenarios = true;
        try {
            const result = await services.Companies.automationsService(
                companyId
            ).scenarios(automationId, params);
            runInAction(() => {
                this.scenarios.replace(result.scenarios || []);
                this.scenariosTotal = result.count || 0;
            });
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.loadingScenarios = false;
        }
    }

    async loadThreatAreas(companyId, automationId, params = {}) {
        const queryKey = JSON.stringify({ companyId, automationId, params });
        if (this.loadingThreatAreas && this.threatAreasQueryKey === queryKey) return;
        this.threatAreasQueryKey = queryKey;
        this.loadingThreatAreas = true;
        try {
            const result = await services.Companies.automationsService(
                companyId
            ).threatAreas(automationId, params);
            runInAction(() => {
                this.threatAreas.replace(result.threatAreas || []);
                this.threatAreasTotal = result.count || 0;
            });
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.loadingThreatAreas = false;
        }
    }

    async loadScormUsers(companyId, automationId, params = {}) {
        const queryKey = JSON.stringify({ companyId, automationId, params });
        if (this.loadingScormUsers && this.scormUsersQueryKey === queryKey) return;
        this.scormUsersQueryKey = queryKey;
        this.loadingScormUsers = true;
        try {
            const result = await services.Companies.automationsService(companyId)
                .usersService(automationId)
                .list(params);
            runInAction(() => {
                this.scormUsers.replace(result.users || []);
                this.scormUsersTotal = result.count || 0;
            });
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.loadingScormUsers = false;
        }
    }

    async loadListsProgress(companyId, automationId, params = {}) {
        const queryKey = JSON.stringify({ companyId, automationId, params });
        if (this.loadingListsProgress && this.listsProgressQueryKey === queryKey)
            return;
        this.listsProgressQueryKey = queryKey;
        this.loadingListsProgress = true;
        try {
            const result = await services.Companies.automationsService(
                companyId
            ).lists(automationId, params);
            runInAction(() => {
                this.listsProgress.replace(result.lists || []);
                this.listsProgressTotal = result.count || 0;
            });
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.loadingListsProgress = false;
        }
    }

    async loadTrainingUsers(companyId, automationId, params = {}) {
        const queryKey = JSON.stringify({ companyId, automationId, params });
        if (this.loadingTrainingUsers && this.trainingUsersQueryKey === queryKey)
            return;
        this.trainingUsersQueryKey = queryKey;
        this.loadingTrainingUsers = true;
        try {
            const result = await services.Companies.automationsService(
                companyId
            ).users(automationId, params);
            runInAction(() => {
                this.trainingUsers.replace(result.users || []);
                this.trainingUsersTotal = result.count || 0;
            });
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.loadingTrainingUsers = false;
        }
    }

    async loadRemindersOverview(companyId, automationId, params = {}) {
        const queryKey = JSON.stringify({ companyId, automationId, params });
        if (
            this.loadingRemindersOverview &&
            this.remindersOverviewQueryKey === queryKey
        )
            return;
        this.remindersOverviewQueryKey = queryKey;
        this.loadingRemindersOverview = true;
        try {
            const result = await services.Companies.automationsService(
                companyId
            ).remindersOverview(automationId, params);
            runInAction(() => {
                this.remindersOverview.replace(result.reminders || []);
                this.remindersOverviewTotal = result.count || 0;
            });
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.loadingRemindersOverview = false;
        }
    }

    async loadScheduleOverview(companyId, automationId) {
        if (this.loadingScheduleOverview) return;
        this.loadingScheduleOverview = true;
        try {
            const result = await services.Companies.automationsService(
                companyId
            ).schedule(automationId);
            runInAction(() => {
                this.scheduleOverview = result;
            });
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.loadingScheduleOverview = false;
        }
    }

    async updateScheduleEvent(companyId, automationId, event) {
        if (this.updatingScheduleEvents.includes(event.id)) return;
        this.updatingScheduleEvents.push(event.id);
        try {
            await services.Companies.automationsService(
                companyId
            ).updateScheduleEvent(automationId, event.id, {
                status: event.status,
                send: event.send,
            });
            runInAction(() => {
                event.dirty = false;
                event.originalData = {
                    ...event,
                    dirty: false,
                    originalData: undefined,
                };
            });
            return true;
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.updatingScheduleEvents.remove(event.id);
        }
    }

    async loadAutomation(companyId, automationId) {
        if (this.loadingAutomation) return;
        this.loadingAutomation = true;
        this.error = null;
        this.chartMetrics.replace([]);
        this.chartMetricsMigrating = false;
        try {
            await this.catalogStore.loadCatalog(companyId);

            let questions = await services.Companies.questionsService(
                companyId
            ).list();
            this.questions.replace(questions);

            await this.listStore.loadCompanyLists(companyId);

            const result = await services.Companies.automationsService(
                companyId
            ).fetch(automationId);

            this.commonStore.analyticsEvent(
                'Training',
                'load',
                `company-${companyId}-automation-${automationId}`
            );

            this.editingAutomation = new Automation(
                this.listStore,
                this.catalogStore,
                this.mainStore,
                companyId,
                this.questionsIndex,
                result,
                this.mainStore.isTrial
            );
            this.perUserDetails.replace([]);
            this.perUserDetailsLoaded = false;
            this.perUserDetailsEmails = [];
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.loadingAutomation = false;
        }
        return !!this.error;
    }

    async loadUsersDetails(companyId, automationId, emails = []) {
        try {
            const result = await services.Companies.automationsService(
                companyId
            ).loadUsersDetails(
                automationId,
                emails.length > 0 ? { emails: emails.join(',') } : undefined
            );
            this.perUserDetails.replace(result);
            this.perUserDetailsLoaded = true;
            this.perUserDetailsEmails = emails;
        } catch (e) {
            this.error = extractErrorMessage(e);
        }
    }

    loadSigninLink = generateLoadEntity(
        'loadSigninLink',
        this,
        'loadingSigninLink',
        async function (companyId, automationId, email) {
            return await services.Companies.automationsService(
                companyId
            ).getSigninLink(automationId, email);
        },
        'lastSigninLinkUrl',
        'url',
        undefined,
        { clearResultBeforeLoad: true }
    );

    async resendEventMail(companyId, automationId, eventId, email) {
        if (this.sendAutomationEmails.includes(email)) return;
        this.sendAutomationEmails.push(email);
        try {
            await services.Companies.automationsService(
                companyId
            ).resendEventMail(automationId, eventId, email);
            this.commonStore.success('Reminder sent.');
            this.commonStore.analyticsEvent(
                'Training',
                'resend-email',
                `company-${companyId}-automation-${automationId}`
            );
            let user =
                this.automationStatus &&
                this.automationStatus.users &&
                this.automationStatus.users.find((x) => x.email === email);
            if (user) user.sent = true;
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.sendAutomationEmails.remove(email);
        }
    }

    async loadEventUserDetails(companyId, automationId, eventId, email) {
        if (this.loadingAutomationUserDetails) return;
        this.loadingAutomationUserDetails = true;
        try {
            this.automationUserStatus =
                await services.Companies.automationsService(
                    companyId
                ).eventUserStatus(automationId, eventId, email);
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.loadingAutomationUserDetails = false;
        }
    }

    async loadUserDetails(companyId, trainingId, email) {
        if (this.loadingAutomationUserDetails) return;
        this.loadingAutomationUserDetails = true;
        try {
            this.automationUserStatus =
                await services.Companies.automationsService(
                    companyId
                ).userStatus(trainingId, email);
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.loadingAutomationUserDetails = false;
        }
    }

    async delete(companyId, automationId) {
        if (this.deletingAutomation) return;
        this.deletingAutomation = true;
        try {
            await services.Companies.automationsService(companyId).delete(
                automationId
            );
            this.editingAutomation = null;
            this.automation = null;
            this.automations.replace(
                // SCORM ids arrive prefixed (p<id>) while the list stores the numeric id,
                // so match both forms to actually drop the deleted row from the in-memory list.
                this.automations.filter(
                    (x) => x.id !== automationId && `p${x.id}` !== automationId
                )
            );
            return true;
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.deletingAutomation = false;
        }
    }

    async startAutomation(companyId, extraData) {
        if (this.creatingAutomation) return;
        this.creatingAutomation = true;
        let result;

        try {
            if (this.automation.id) {
                result = await services.Companies.automationsService(
                    companyId
                ).update(
                    this.automation.toDTO({
                        ...extraData,
                        templateId: this.selectedTemplates,
                    })
                );
            } else {
                result = await services.Companies.automationsService(
                    companyId
                ).create(
                    this.automation.toDTO({
                        ...extraData,
                        templateId: this.selectedTemplates,
                    })
                );
            }

            this.commonStore.analyticsEvent(
                'trainings',
                'create',
                `company-${companyId}`
            );

            // 'await' intentionally missing
            this.sendAutomationCreatedEvent(result.automation.id);

            return result.automation;
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.creatingAutomation = false;
        }
    }

    async saveDraft(companyId, extraData) {
        if (this.savingDraft) return;
        this.savingDraft = true;

        try {
            if (this.automation.id) {
                await services.Companies.automationsService(
                    companyId
                ).updateDraft(
                    this.automation.toDTO({
                        ...extraData,
                        templateId: this.selectedTemplates,
                    })
                );
            } else {
                let result = await services.Companies.automationsService(
                    companyId
                ).saveDraft(
                    this.automation.toDTO({
                        ...extraData,
                        templateId: this.selectedTemplates,
                    })
                );
                if (result.success) this.automation.setId(result.automation.id);
            }
            return true;
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.savingDraft = false;
        }
    }

    async pauseAutomation(companyId, automationId, isAssesmentList) {
        if (this.updatingAutomation) return;
        this.updatingAutomation = true;
        try {
            await services.Companies.automationsService(
                companyId
            ).pauseAutomation(automationId);
            if (isAssesmentList) {
                let respectiveAutomation = this.automations.find(
                    (x) => x.id === automationId
                );
                respectiveAutomation.status = STATUS.STOPPED;
            } else {
                this.editingAutomation.status = STATUS.STOPPED;
            }
            return true;
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.updatingAutomation = false;
        }
    }

    async resumeAutomation(companyId, automationId, isAssesmentList) {
        if (this.updatingAutomation) return;
        this.updatingAutomation = true;
        try {
            const { newStatus } = await services.Companies.automationsService(
                companyId
            ).resumeAutomation(automationId);
            if (isAssesmentList) {
                let respectiveAutomation = this.automations.find(
                    (x) => x.id === automationId
                );
                respectiveAutomation.status = newStatus;
            } else {
                this.editingAutomation.status = newStatus;
            }
            return true;
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.updatingAutomation = false;
        }
    }

    get wizardActionButtonText() {
        if (this.step === AUTOMATION_WIZARD_STEPS.FINALIZE) {
            if (this.automation && this.automation.id) {
                if (this.automation.status === STATUS.DRAFT) return 'Activate';
                return 'Update';
            }
            return 'Create';
        }
        return 'Continue';
    }

    get perUserDetailsIndex() {
        if (!this.perUserDetailsLoaded) return null;

        return keyBy(this.perUserDetails, 'email');
    }

    get questionsIndex() {
        return keyBy(this.questions, 'question_id');
    }

    async sendAutomationCreatedEvent(trainingId) {
        await this.commonStore.saveAnalyticsEvent('training', 'created', {
            trainingId,
            totalSubjects: this.automation.selectedVideosIds.length,
            deliveryType: translateTrainingScheduleType(
                this.automation.scheduleType
            ),
            deliveryCadence: this.automation.interval,
            subjectDeliveryChannel: getDeliveryChannels(
                this.automation.channel
            ),
            subjectsPerInterval:
                parseInt(this.automation.subjectsPerInterval) || 0,
            totalReminders: this.automation.reminders_steps.length,
            totalEmployees: this.automation.selectedUserCount,
            employeeListIds: [...this.automation.selectedUserListsIds],
            programName: (() => {
                const selected =
                    this.selectedTemplates.length > 0
                        ? this.selectedTemplates[0]
                        : null;
                if (!selected) return '';
                const template = this.rawTemplates.find(
                    (t) => t.id === selected
                );
                return template ? template.title : '';
            })(),
            programId:
                this.selectedTemplates.length > 0
                    ? `${this.selectedTemplates[0]}`
                    : '',
            subjectIds: [...this.automation.selectedVideosIds],
            startDate: new Date(
                this.automation.deliverySchedule[0].date
            ).toISOString(),
            endDate: (() => {
                const endDate = new Date(
                    this.automation.deliverySchedule[
                        this.automation.deliverySchedule.length - 1
                    ].date
                );
                endDate.setDate(endDate.getDate() + 7);
                return endDate.toISOString();
            })(),
        });
    }

    async rename(companyId, automationId, data) {
        if (this.updatingAutomation) return;
        this.updatingAutomation = true;
        try {
            await services.Companies.automationsService(
                companyId
            ).renameAutomation(automationId, data);
            this.editingAutomation.name = data.name;
            if (data.nameTranslations !== undefined) {
                this.editingAutomation.nameTranslations =
                    data.nameTranslations || {};
            }
            return true;
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.updatingAutomation = false;
        }
    }

    get demoTrainingURL() {
        if (!this.mainStore.currentCompany || !this.authStore.currentUser)
            return null;
        const reportTo = `"${this.authStore.currentUser.name}" <${this.authStore.currentUser.email}>`;
        return `${this.brandingStore.myDomain}/training/try-demo/${
            this.mainStore.currentCompany.company_uid
        }/${Buffer.from(reportTo, 'utf8').toString('base64')}`;
    }

    updateDue = generateUpdateEntity(
        'updateDue',
        this,
        'updatingDue',
        async (companyId, automationId, due) => {
            await services.Companies.automationsService(companyId).updateDue(
                automationId,
                { due }
            );
            return true;
        }
    );

    saveRemindersSettings = generateUpdateEntity(
        'saveRemindersSettings',
        this,
        'updatingRemindersSettings',
        async (companyId, automationId, body) => {
            const result = await services.Companies.automationsService(
                companyId
            ).updateRemindersSettings(automationId, body);
            runInAction(() => {
                if (this.editingAutomation) {
                    this.editingAutomation.reminders_enabled = Boolean(
                        result.reminders_enabled
                    );
                    if (result.reminders_settings?.channel != null) {
                        this.editingAutomation.reminders_channel =
                            result.reminders_settings.channel;
                    }
                    if (Array.isArray(result.reminders_steps)) {
                        if (result.reminders_steps.length > 0) {
                            this.editingAutomation.reminders_steps.replace(
                                result.reminders_steps
                            );
                        } else {
                            this.editingAutomation.reminders_steps.replace([
                                {
                                    delay: 1,
                                    delayType: 3,
                                },
                            ]);
                        }
                    }
                    if (this.editingAutomation._rawData) {
                        this.editingAutomation._rawData.reminders_enabled =
                            result.reminders_enabled;
                        this.editingAutomation._rawData.reminders_settings =
                            result.reminders_settings;
                        this.editingAutomation._rawData.reminders_steps =
                            result.reminders_steps;
                    }
                }
            });
            this.loadingScheduleOverview = false;
            await this.loadScheduleOverview(companyId, automationId);
            return true;
        }
    );

    switchSyncLists = generateUpdateEntity(
        'switchSyncLists',
        this,
        'switchingSyncLists',
        async (companyId, automationId, desiredState) => {
            await services.Companies.automationsService(
                companyId
            ).switchSyncLists(automationId, desiredState);
            runInAction(() => {
                this.editingAutomation.disableSyncLists = desiredState;
            });
            return true;
        }
    );

    previewSyncLists = [];
    loadPreviewSyncLists = generateLoadList(
        'previewSyncLists',
        this,
        'loadingPreviewSyncLists',
        async (companyId, automationId) => {
            return await services.Companies.automationsService(
                companyId
            ).previewSyncLists(automationId);
        },
        'previewSyncLists'
    );

    syncUsers = generateUpdateEntity(
        'syncUsers',
        this,
        'syncingUsers',
        async (companyId, automationId) => {
            await services.Companies.automationsService(companyId).syncUsers(
                automationId,
                {
                    added: {
                        ...this.previewUsersToAddSelection.selectionContext,
                        pageSize: this.toAddPageData.pageSize,
                    },
                    removed: {
                        ...this.previewUsersToRemoveSelection.selectionContext,
                        pageSize: this.toRemovePageData.pageSize,
                    },
                }
            );
            return true;
        }
    );

    get previewUsersToAdd() {
        return this.previewSyncLists.filter((x) => x.added);
    }

    get previewUsersToRemove() {
        return this.previewSyncLists.filter((x) => x.removed);
    }

    previewUsersToAddQuery = '';
    previewUsersToRemoveQuery = '';

    toAddPageData = {
        pageIndex: 0,
        pageSize: 5,
    };

    toRemovePageData = {
        pageIndex: 0,
        pageSize: 5,
    };

    setPreviewUsersToAddQuery(query) {
        this.previewUsersToAddQuery = query || '';
        this.toAddPageData.pageIndex = 0;
    }

    setPreviewUsersToRemoveQuery(query) {
        this.previewUsersToRemoveQuery = query || '';
        this.toRemovePageData.pageIndex = 0;
    }

    get previewUsersToAddFiltered() {
        return filterPreviewSyncUsers(
            this.previewUsersToAdd,
            this.previewUsersToAddQuery
        );
    }

    get previewUsersToRemoveFiltered() {
        return filterPreviewSyncUsers(
            this.previewUsersToRemove,
            this.previewUsersToRemoveQuery
        );
    }

    setToAddPageData(pageIndex, pageSize) {
        this.toAddPageData.pageIndex = pageIndex;
        this.toAddPageData.pageSize = pageSize;
    }

    setToRemovePageData(pageIndex, pageSize) {
        this.toRemovePageData.pageIndex = pageIndex;
        this.toRemovePageData.pageSize = pageSize;
    }

    setEnabledLanguages(languages) {
        this.automation.enabledLanguages = languages;
    }

    get previewUsersToAddCurrentPage() {
        const { pageIndex, pageSize } = this.toAddPageData;
        const list = this.previewUsersToAddFiltered;
        return list.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize);
    }

    get previewUsersToRemoveCurrentPage() {
        const { pageIndex, pageSize } = this.toRemovePageData;
        const list = this.previewUsersToRemoveFiltered;
        return list.slice(
            pageIndex * pageSize,
            pageIndex * pageSize + pageSize
        );
    }
}

export default AutomationStore;
